import { css } from 'styled-components';

// One button, three sizes. Hover is the offset-shadow displacement rather
// than a colour change. inline-flex + centring makes the label sit optically
// centred regardless of the font's ascender/descender metrics — padding alone
// leaves it high, which is what the hardcoded values used to compensate for.
const buttonBase = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--text-emphasis);
  background-color: transparent;
  border: 1px solid var(--border-strong);
  border-radius: var(--border-radius);
  font-family: var(--font-sans);
  font-weight: 500;
  line-height: 1;
  text-align: center;
  text-decoration: none;
  transition: var(--transition);

  /* Displacement at 120 degrees (polar, measured anticlockwise from the
     positive x-axis) over 6px: cos120 x 6 = -3, sin120 x 6 = 5.196, negated on
     y because screen coordinates run downwards. The shadow takes the opposite
     offset so it reads as the button lifting off a fixed surface. Previously
     this was -4,-4, which is 135 degrees. */
  &:hover,
  &:focus-visible {
    color: var(--text);
    background-color: transparent;
    border-color: var(--accent);
    box-shadow: 3px 5.2px 0 0 var(--accent);
    transform: translate(-3px, -5.2px);
    outline: none;
  }

  &:after {
    display: none !important;
  }
`;

const mixins = {
  flexCenter: css`
    display: flex;
    justify-content: center;
    align-items: center;
  `,

  flexBetween: css`
    display: flex;
    justify-content: space-between;
    align-items: center;
  `,

  link: css`
    display: inline-block;
    text-decoration: none;
    text-decoration-skip-ink: auto;
    color: inherit;
    position: relative;
    transition: var(--transition);

    &:hover,
    &:focus-visible {
      color: var(--accent);
      outline: 0;
    }
  `,

  inlineLink: css`
    display: inline-block;
    position: relative;
    color: var(--accent);
    transition: var(--transition);

    /* Hover is the underline sweep only — the colour stays put. */
    &:hover,
    &:focus-visible {
      color: var(--accent);
      outline: 0;
      &:after {
        width: 100%;
      }
    }
    &:after {
      content: '';
      display: block;
      width: 0;
      height: 1px;
      position: relative;
      bottom: 0.37em;
      background-color: currentColor;
      @media (prefers-reduced-motion: no-preference) {
        transition: var(--transition);
      }
    }
  `,

  /* Vertical padding is deliberately asymmetric. Flexbox centres the line
     box, but Calibre's cap-height band sits ~0.17em above that line box's
     centre, so a symmetrically padded label reads high. Adding the offset to
     the top and removing it from the bottom shifts the glyphs down by 0.17em
     while leaving the button's overall height untouched. */
  button: css`
    ${buttonBase};
    padding: calc(var(--space-3) + var(--optical)) var(--space-5)
      calc(var(--space-3) - var(--optical));
    font-size: var(--fz-md);
  `,

  smallButton: css`
    ${buttonBase};
    padding: calc(var(--space-2) + var(--optical)) var(--space-4)
      calc(var(--space-2) - var(--optical));
    font-size: var(--fz-sm);
  `,

  bigButton: css`
    ${buttonBase};
    padding: calc(var(--space-4) + var(--optical)) var(--space-6)
      calc(var(--space-4) - var(--optical));
    font-size: var(--fz-md);
  `,

  boxShadow: css`
    box-shadow: 0 10px 30px -15px var(--shadow);
    transition: var(--transition);

    &:hover,
    &:focus-visible {
      box-shadow: 0 20px 30px -15px var(--shadow);
    }
  `,

  /* The accent triangle list. One definition: GlobalStyle's .fancy-list class
     is this mixin, so the two cannot drift, which they had — the class used
     --lh-body and the mixin a literal 1.6 for the same list. */
  fancyList: css`
    padding: 0;
    margin: 0;
    list-style: none;
    font-size: var(--fz-lg);

    li {
      position: relative;
      padding-left: var(--space-5);
      margin-bottom: var(--space-2);
      line-height: var(--lh-body);

      &:before {
        content: '▹';
        position: absolute;
        left: 0;
        line-height: inherit;
        color: var(--accent);
      }
    }
  `,

  /* Makes a panel a sheet of glass rather than a filled rectangle.
     ------------------------------------------------------------------------
     Both declarations resolve to `none` in the dark theme, where surfaces are
     opaque and there is nothing behind them worth showing, so carrying this is
     free there — a `backdrop-filter: none` does not even establish a
     containing block, which is the one way it could have changed dark layout.

     In the light theme it is the effect: the panel is a 60% white sheet and
     the blur pulls the grain, the dot lattice and the pointer halo through it
     so the page is visible but out of focus underneath. Without the blur the
     same translucency just looks like a slightly wrong shade of white. */
  glass: css`
    -webkit-backdrop-filter: var(--glass-blur);
    backdrop-filter: var(--glass-blur);
  `,

  /* The same, plus the lift. Only for panels that do not already carry
     mixins.boxShadow — that one is built on --shadow and adapts on its own. */
  glassPanel: css`
    -webkit-backdrop-filter: var(--glass-blur);
    backdrop-filter: var(--glass-blur);
    box-shadow: var(--glass-shadow);
  `,

  resetList: css`
    list-style: none;
    padding: 0;
    margin: 0;
  `,
};

export default mixins;
